import { Input } from '@thanx/uikit/input'
import React from 'react'

// Allows partial numeric entry with at most two decimal places: '', '12',
// '12.', '12.3', '12.34' all pass while '12.345' and non-numeric input are
// rejected, so the field can never hold more than cents precision.
const TWO_DECIMAL_PATTERN = /^\d*(\.\d{0,2})?$/

type Props = {
  id: string
  ariaLabel: string
  value: string
  onChange: (value: string) => void
}

// Masking is deliberately hand-rolled: the uikit format/formatCurrency
// helpers pad eagerly mid-entry ('3.' -> '3.00'), which fights exactly the
// cent-level typing this input exists for — don't simplify into them.
const MoneyInput = ({ id, ariaLabel, value, onChange }: Props) => {
  const handleChange = (next: string) => {
    // Accept pasted values dressed as currency ('$3.25', '1,234.56') by
    // stripping the dressing before validating.
    const bare = next.replace(/[$,\s]/g, '')
    if (TWO_DECIMAL_PATTERN.test(bare)) onChange(bare)
  }

  // Normalize to two decimal places when the field loses focus: '12.5' ->
  // '12.50'. An empty field stays empty; a stray '.' clears.
  const handleBlur = () => {
    if (value === '') return
    const parsed = parseFloat(value)
    onChange(Number.isNaN(parsed) ? '' : parsed.toFixed(2))
  }

  // Up/Down keep the whole-dollar stepping the replaced number input had
  // (step=1), now preserving cents (12.50 -> 13.50), clamped at zero.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    e.preventDefault()
    const current = parseFloat(value)
    const base = Number.isNaN(current) ? 0 : current
    const delta = e.key === 'ArrowUp' ? 1 : -1
    onChange(Math.max(0, base + delta).toFixed(2))
  }

  return (
    <Input
      id={id}
      aria-label={ariaLabel}
      inputMode="decimal"
      startEnhancer="$"
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  )
}

export default MoneyInput
